import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search, KeyRound, Mail, GraduationCap,
  Users, X, Eye, EyeOff, RefreshCw, Pencil,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Modal from '../components/layout/Modal';

const inputCls =
  'w-full px-3 py-2.5 bg-white border border-[#E2E0D8] rounded-xl text-[13px] ' +
  'text-[#1a1a18] placeholder-[#B4B2A9] focus:outline-none focus:ring-2 ' +
  'focus:ring-[#1a1a18]/10 focus:border-[#888780] transition-colors';
const labelCls = 'block text-[11px] font-medium text-[#888780] uppercase tracking-wider mb-1.5';

const PAGE_SIZE = 15;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function generatePassword(length = 12) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
  return Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

function Avatar({ name, size = 'md' }) {
  const initials = name
    ? name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
    : '?';
  const colors = [
    ['#E6F1FB', '#185FA5'], ['#EAF3DE', '#3B6D11'],
    ['#F3EBF9', '#6B21A8'], ['#FEF3C7', '#92400E'],
    ['#FCE7F3', '#9D174D'], ['#F1EFE8', '#5F5E5A'],
  ];
  const [bg, fg] = colors[(name?.charCodeAt(0) ?? 0) % colors.length];
  const sz = size === 'lg' ? 'w-10 h-10 text-[13px]' : 'w-8 h-8 text-[11px]';
  return (
    <div
      className={`${sz} rounded-xl flex items-center justify-center font-semibold flex-shrink-0`}
      style={{ background: bg, color: fg }}
    >
      {initials}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function StudentsPage() {
  const qc = useQueryClient();
  const [search, setSearch]           = useState('');
  const [page, setPage]               = useState(1);
  const [resetModal, setResetModal]   = useState({ open: false, student: null });
  const [editModal, setEditModal]     = useState({ open: false, student: null });

  const { data: students = [], isLoading } = useQuery(
    ['students'],
    () => api.get('/students').then(r => r.data)
  );

  const filtered = students.filter(s => {
    const q = search.toLowerCase();
    return (
      s.full_name?.toLowerCase().includes(q) ||
      s.email?.toLowerCase().includes(q) ||
      s.student_number?.toLowerCase().includes(q) ||
      s.department?.toLowerCase().includes(q)
    );
  });

  // Reset to page 1 when search changes
  const handleSearch = (val) => { setSearch(val); setPage(1); };

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetMutation = useMutation(
    ({ id, newPassword }) => api.post(`/students/${id}/reset-password`, { newPassword }),
    {
      onSuccess: () => {
        toast.success('Password reset & email sent');
        setResetModal({ open: false, student: null });
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to reset password'),
    }
  );

  const editMutation = useMutation(
    ({ id, data }) => api.put(`/students/${id}`, data),
    {
      onSuccess: () => {
        qc.invalidateQueries(['students']);
        toast.success('Student updated');
        setEditModal({ open: false, student: null });
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to update student'),
    }
  );

  return (
    <div className="min-h-screen bg-[#F7F6F2]">

      {/* Header */}
      <header className="bg-[#F7F6F2] border-b border-[#E2E0D8] px-8 py-4
                         flex items-center justify-between sticky top-0 z-10">
        <div>
          <p className="text-[10px] text-[#B4B2A9] uppercase tracking-[0.12em] mb-0.5">
            Timetabling System
          </p>
          <h1 className="text-[18px] font-medium text-[#1a1a18] leading-none">Students</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white border border-[#E2E0D8] rounded-xl">
            <Users className="w-3.5 h-3.5 text-[#B4B2A9]" />
            <span className="text-[12px] text-[#5F5E5A] font-medium">
              {students.length} {students.length === 1 ? 'student' : 'students'}
            </span>
          </div>
        </div>
      </header>

      <main className="px-8 py-7 max-w-7xl mx-auto">

        {/* Search */}
        <div className="relative mb-5 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#B4B2A9]" />
          <input
            type="text"
            placeholder="Search by name, email, number, department…"
            value={search}
            onChange={e => handleSearch(e.target.value)}
            className="w-full pl-9 pr-8 py-2 bg-white border border-[#E2E0D8] rounded-xl
                       text-[13px] text-[#1a1a18] placeholder-[#B4B2A9]
                       focus:outline-none focus:border-[#888780] transition-colors"
          />
          {search && (
            <button
              onClick={() => handleSearch('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5
                         text-[#B4B2A9] hover:text-[#1a1a18] transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>

        {/* Table */}
        <div className="bg-white border border-[#E2E0D8] rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#F1EFE8]">
                {['Student', 'Email', 'Student No.', 'Department', 'Year', ''].map(h => (
                  <th key={h} className="text-left text-[10px] font-medium text-[#888780]
                                         uppercase tracking-widest px-5 py-3.5">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="text-center text-[13px] text-[#B4B2A9] py-16">
                    Loading…
                  </td>
                </tr>
              ) : paginated.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <div className="flex flex-col items-center gap-3 py-16">
                      <div className="w-10 h-10 rounded-xl bg-[#F1EFE8] flex items-center justify-center">
                        <GraduationCap className="w-5 h-5 text-[#B4B2A9]" />
                      </div>
                      <p className="text-[13px] text-[#B4B2A9]">
                        {search ? 'No students match your search' : 'No students found'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : paginated.map((s, i) => (
                <tr
                  key={s.id}
                  className={`group hover:bg-[#FAFAF8] transition-colors
                    ${i < paginated.length - 1 ? 'border-b border-[#F1EFE8]' : ''}`}
                >
                  {/* Name + Avatar */}
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <Avatar name={s.full_name} />
                      <p className="text-[13px] font-medium text-[#1a1a18]">{s.full_name}</p>
                    </div>
                  </td>

                  {/* Email */}
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-1.5 text-[13px] text-[#5F5E5A]">
                      <Mail className="w-3 h-3 text-[#B4B2A9] flex-shrink-0" />
                      {s.email}
                    </div>
                  </td>

                  {/* Student Number */}
                  <td className="px-5 py-3.5">
                    {s.student_number ? (
                      <span className="px-2.5 py-1 bg-[#F1EFE8] text-[#5F5E5A]
                                       text-[11px] font-mono font-medium rounded-lg">
                        {s.student_number}
                      </span>
                    ) : (
                      <span className="text-[#D3D1C7]">—</span>
                    )}
                  </td>

                  {/* Department */}
                  <td className="px-5 py-3.5 text-[13px] text-[#5F5E5A]">
                    {s.department || <span className="text-[#D3D1C7]">—</span>}
                  </td>

                  {/* Year of Study */}
                  <td className="px-5 py-3.5">
                    {s.year_of_study ? (
                      <span className="px-2.5 py-1 bg-[#E6F1FB] text-[#185FA5]
                                       text-[11px] font-medium rounded-lg">
                        Year {s.year_of_study}
                      </span>
                    ) : (
                      <span className="text-[#D3D1C7]">—</span>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-1
                                    opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => setEditModal({ open: true, student: s })}
                        className="p-1.5 text-[#888780] hover:text-[#1a1a18]
                                   hover:bg-[#F1EFE8] rounded-lg transition-colors"
                        title="Edit student"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setResetModal({ open: true, student: s })}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-medium
                                   text-[#888780] hover:text-[#1a1a18] hover:bg-[#F1EFE8]
                                   rounded-lg transition-colors"
                      >
                        <KeyRound className="w-3.5 h-3.5" />
                        Reset password
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Footer: count + pagination */}
          <div className="border-t border-[#F1EFE8] px-5 py-3 flex items-center justify-between">
            <p className="text-[11px] text-[#B4B2A9]">
              {filtered.length === 0
                ? 'No students'
                : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, filtered.length)} of ${filtered.length} ${filtered.length === 1 ? 'student' : 'students'}${search ? ` matching "${search}"` : ''}`}
            </p>

            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-1.5 text-[#888780] hover:text-[#1a1a18] hover:bg-[#F1EFE8]
                             disabled:opacity-30 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
                  .reduce((acc, n, idx, arr) => {
                    if (idx > 0 && n - arr[idx - 1] > 1) acc.push('…');
                    acc.push(n);
                    return acc;
                  }, [])
                  .map((n, idx) =>
                    n === '…' ? (
                      <span key={`ellipsis-${idx}`} className="px-1 text-[11px] text-[#B4B2A9]">…</span>
                    ) : (
                      <button
                        key={n}
                        onClick={() => setPage(n)}
                        className={`w-7 h-7 rounded-lg text-[12px] font-medium transition-colors
                          ${page === n
                            ? 'bg-[#1a1a18] text-[#F7F6F2]'
                            : 'text-[#5F5E5A] hover:bg-[#F1EFE8] hover:text-[#1a1a18]'}`}
                      >
                        {n}
                      </button>
                    )
                  )}

                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-1.5 text-[#888780] hover:text-[#1a1a18] hover:bg-[#F1EFE8]
                             disabled:opacity-30 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Edit Student Modal */}
      <Modal
        isOpen={editModal.open}
        onClose={() => setEditModal({ open: false, student: null })}
        title="Edit student"
      >
        <EditStudentForm
          student={editModal.student}
          loading={editMutation.isLoading}
          onSubmit={(data) => editMutation.mutate({ id: editModal.student.id, data })}
          onCancel={() => setEditModal({ open: false, student: null })}
        />
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        isOpen={resetModal.open}
        onClose={() => setResetModal({ open: false, student: null })}
        title="Reset password"
      >
        <ResetPasswordForm
          student={resetModal.student}
          loading={resetMutation.isLoading}
          onSubmit={({ newPassword }) =>
            resetMutation.mutate({ id: resetModal.student.id, newPassword })
          }
          onCancel={() => setResetModal({ open: false, student: null })}
        />
      </Modal>
    </div>
  );
}

// ─── Edit Student Form ────────────────────────────────────────────────────────

function EditStudentForm({ student, loading, onSubmit, onCancel }) {
  const [form, setForm] = useState({
    full_name:      student?.full_name      ?? '',
    email:          student?.email          ?? '',
    student_number: student?.student_number ?? '',
    department:     student?.department     ?? '',
    year_of_study:  student?.year_of_study  ?? 1,
  });

  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  return (
    <div className="space-y-4 pt-1">

      {/* Avatar preview */}
      <div className="flex items-center gap-3 p-3.5 bg-[#F7F6F2] border border-[#E2E0D8] rounded-xl">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center font-semibold
                        text-[13px] flex-shrink-0 bg-[#E6F1FB] text-[#185FA5]">
          {form.full_name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?'}
        </div>
        <div>
          <p className="text-[13px] font-medium text-[#1a1a18]">{form.full_name || 'Student name'}</p>
          <p className="text-[11px] text-[#B4B2A9] font-mono">{student?.student_number}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className={labelCls}>Full name *</label>
          <input
            required
            value={form.full_name}
            onChange={set('full_name')}
            className={inputCls}
            placeholder="John Banda"
          />
        </div>

        <div className="col-span-2">
          <label className={labelCls}>Email *</label>
          <input
            required
            type="email"
            value={form.email}
            onChange={set('email')}
            className={inputCls}
            placeholder="john@example.com"
          />
        </div>

        <div>
          <label className={labelCls}>Student number</label>
          <input
            value={form.student_number}
            onChange={set('student_number')}
            className={inputCls}
            placeholder="STU2024001"
          />
        </div>

        <div>
          <label className={labelCls}>Year of study</label>
          <select
            value={form.year_of_study}
            onChange={set('year_of_study')}
            className={inputCls}
          >
            {[1, 2, 3, 4, 5].map(y => (
              <option key={y} value={y}>Year {y}</option>
            ))}
          </select>
        </div>

        <div className="col-span-2">
          <label className={labelCls}>Department</label>
          <input
            value={form.department}
            onChange={set('department')}
            className={inputCls}
            placeholder="Computer Science"
          />
        </div>
      </div>

      <div className="flex gap-3 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2.5 bg-white border border-[#E2E0D8] hover:border-[#888780]
                     text-[#5F5E5A] hover:text-[#1a1a18] rounded-xl transition-colors
                     text-[13px] font-medium"
        >
          Cancel
        </button>
        <button
          onClick={() => onSubmit(form)}
          disabled={!form.full_name.trim() || !form.email.trim() || loading}
          className="flex-1 py-2.5 bg-[#1a1a18] hover:bg-[#2C2C2A] disabled:opacity-50
                     text-[#F7F6F2] rounded-xl transition-colors text-[13px] font-medium"
        >
          {loading ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

// ─── Reset Password Form ──────────────────────────────────────────────────────

function ResetPasswordForm({ student, loading, onSubmit, onCancel }) {
  const [password, setPassword] = useState(() => generatePassword());
  const [show, setShow] = useState(false);

  const regenerate = () => setPassword(generatePassword());

  return (
    <div className="space-y-4 pt-1">

      {/* Student info */}
      <div className="flex items-center gap-3 p-3.5 bg-[#F7F6F2] border border-[#E2E0D8] rounded-xl">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center font-semibold
                        text-[13px] flex-shrink-0 bg-[#E6F1FB] text-[#185FA5]">
          {student?.full_name?.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() ?? '?'}
        </div>
        <div>
          <p className="text-[13px] font-medium text-[#1a1a18]">{student?.full_name}</p>
          <p className="text-[12px] text-[#888780]">{student?.email}</p>
          {student?.student_number && (
            <p className="text-[11px] text-[#B4B2A9] font-mono">{student.student_number}</p>
          )}
        </div>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-2.5 p-3 bg-[#E6F1FB] border border-[#93C5FD]
                      rounded-xl text-[12px] text-[#185FA5]">
        <Mail className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <p>The new password will be emailed to the student automatically.</p>
      </div>

      {/* Password field */}
      <div>
        <label className={labelCls}>New password</label>
        <div className="relative">
          <input
            type={show ? 'text' : 'password'}
            value={password}
            onChange={e => setPassword(e.target.value)}
            className={inputCls + ' pr-20 font-mono'}
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            <button
              type="button"
              onClick={() => setShow(p => !p)}
              className="p-1.5 text-[#B4B2A9] hover:text-[#1a1a18] hover:bg-[#F1EFE8]
                         rounded-lg transition-colors"
            >
              {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
            <button
              type="button"
              onClick={regenerate}
              title="Generate new password"
              className="p-1.5 text-[#B4B2A9] hover:text-[#1a1a18] hover:bg-[#F1EFE8]
                         rounded-lg transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        <p className="text-[11px] text-[#B4B2A9] mt-1.5">
          Edit manually or click ↺ to generate a new one.
        </p>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2.5 bg-white border border-[#E2E0D8] hover:border-[#888780]
                     text-[#5F5E5A] hover:text-[#1a1a18] rounded-xl transition-colors
                     text-[13px] font-medium"
        >
          Cancel
        </button>
        <button
          onClick={() => password.trim() && onSubmit({ newPassword: password.trim() })}
          disabled={!password.trim() || loading}
          className="flex-1 py-2.5 bg-[#1a1a18] hover:bg-[#2C2C2A] disabled:opacity-50
                     text-[#F7F6F2] rounded-xl transition-colors text-[13px] font-medium
                     flex items-center justify-center gap-2"
        >
          {loading
            ? 'Sending…'
            : <><KeyRound className="w-3.5 h-3.5" /> Reset & send email</>}
        </button>
      </div>
    </div>
  );
}